import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MultiSelect } from 'primeng/multiselect';
import { finalize } from 'rxjs';
import { InventoryTransactionTypeEntity } from '../inventory-transaction-type.model';
import { InventoryTransactionTypeService } from '../inventory-transaction-type.service';

@Component({
    selector: 'app-inventory-transaction-type-multi-select',
    imports: [FormsModule, MultiSelect],
    templateUrl: './inventory-transaction-type-multi-select.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InventoryTransactionTypeMultiSelectComponent {
    private destroyRef = inject(DestroyRef);
    private inventoryTransactionTypeService = inject(InventoryTransactionTypeService);

    selectedInventoryTransactionTypeKeys = model<number[]>([]);

    id = input<string>('inventoryTransactionTypeMultiSelect');
    placeHolder = input<string | undefined>('Select transaction types');

    subscriberKey = input.required<number | null>();

    invalid = input<boolean>(false);
    disabled = input<boolean>(false);

    options = signal<InventoryTransactionTypeEntity[]>([]);
    isLoading = signal<boolean>(false);
    loadingChange = output<boolean>();

    blur = output();

    isDisabled = computed(() => this.isLoading() || this.disabled());
    isInvalid = computed(() => this.invalid() && !this.isDisabled());

    constructor() {
        effect(() => {
            const subscriberKey = this.subscriberKey();

            if (subscriberKey == null) {
                this.options.set([]);
            } else {
                this.loadOptions(subscriberKey);
            }
        });

        effect(() => {
            this.loadingChange.emit(this.isLoading());
        });
    }

    loadOptions(subscriberKey: number) {
        this.isLoading.set(true);
        this.options.set([]);

        this.inventoryTransactionTypeService
            .getInventoryTransactionTypes(subscriberKey)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => {
                    this.isLoading.set(false);
                })
            )
            .subscribe((types) => {
                const sorted = types.sort((a, b) => a.inventoryTransactionTypeName.localeCompare(b.inventoryTransactionTypeName));
                this.options.set(sorted);
            });
    }

    onModelChange(keys: number[] | null) {
        this.selectedInventoryTransactionTypeKeys.set(keys ?? []);
    }
}
